import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, X, Save, Image as ImageIcon } from 'lucide-react';
import { variationAPI } from '../services/api';
import { compressImage } from '../utils/imageCompression';
import logger from '../utils/logger';
import ImageGalleryPage from '../pages/ImageGalleryPage';
import ConfirmModal from './ConfirmModal';
import './VariationManagement.css';

const VariationManagement = ({ productId, variations: initialVariations = [], onVariationsChange }) => {
  const [variations, setVariations] = useState(initialVariations);
  const [editingVariation, setEditingVariation] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [galleryMode, setGalleryMode] = useState('main'); // 'main' or 'gallery'
  const [pendingDeleteId, setPendingDeleteId] = useState(null); // Track variation pending deletion

  useEffect(() => {
    setVariations(initialVariations);
  }, [initialVariations]);

  const [formData, setFormData] = useState({
    sku: '',
    attributes: {},
    price: '',
    stock: '',
    mainImage: '',
    images: [],
    description: '',
    metadata: {},
    isDefault: false
  });

  const [newAttributeKey, setNewAttributeKey] = useState('');
  const [newAttributeValue, setNewAttributeValue] = useState('');

  const resetForm = () => {
    setFormData({
      sku: '',
      attributes: {},
      price: '',
      stock: '',
      mainImage: '',
      images: [],
      description: '',
      metadata: {},
      isDefault: false
    });
    setNewAttributeKey('');
    setNewAttributeValue('');
    setError(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAttributeAdd = () => {
    if (newAttributeKey && newAttributeValue) {
      setFormData(prev => ({
        ...prev,
        attributes: {
          ...prev.attributes,
          [newAttributeKey]: newAttributeValue
        }
      }));
      setNewAttributeKey('');
      setNewAttributeValue('');
    }
  };

  const handleAttributeRemove = (key) => {
    setFormData(prev => {
      const newAttributes = { ...prev.attributes };
      delete newAttributes[key];
      return { ...prev, attributes: newAttributes };
    });
  };

  const handleImageUpload = async (e, isMain = false) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      // compressImage already returns a base64 string, not a File/Blob
      const compressedBase64 = await compressImage(file);
      
      if (isMain) {
        setFormData(prev => ({ ...prev, mainImage: compressedBase64 }));
      } else {
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, compressedBase64]
        }));
      }
      setLoading(false);
    } catch (err) {
      logger.error('Error compressing image:', err);
      setError('Failed to process image');
      setLoading(false);
    }
  };

  const handleImageRemove = (index, isMain = false) => {
    if (isMain) {
      setFormData(prev => ({ ...prev, mainImage: '' }));
    } else {
      setFormData(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      if (Object.keys(formData.attributes).length === 0) {
        setError('At least one attribute is required');
        setLoading(false);
        return;
      }

      if (!formData.price || parseFloat(formData.price) <= 0) {
        setError('Valid price is required');
        setLoading(false);
        return;
      }

      if (formData.stock === '' || parseInt(formData.stock) < 0) {
        setError('Valid stock count is required');
        setLoading(false);
        return;
      }

      // Ensure all required fields are properly formatted
      const variationData = {
        product: productId,
        sku: formData.sku || '', // Allow empty SKU, backend will generate
        attributes: formData.attributes || {},
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock) || 0,
        images: formData.images || [],
        mainImage: formData.mainImage || '',
        description: formData.description || '',
        metadata: formData.metadata || {},
        isDefault: formData.isDefault || false
      };

      // Remove empty SKU if not provided (let backend generate it)
      if (!variationData.sku || variationData.sku.trim() === '') {
        delete variationData.sku;
      }

      let savedVariation;
      if (editingVariation) {
        savedVariation = await variationAPI.updateVariation(editingVariation._id, variationData);
      } else {
        savedVariation = await variationAPI.createVariation(variationData);
      }

      // Refresh variations
      const response = await variationAPI.getProductVariations(productId);
      const updatedVariations = response.data.map(v => {
        // Handle attributes - convert Map to object if needed, otherwise use as-is
        let attributes = v.attributes;
        if (attributes instanceof Map) {
          attributes = Object.fromEntries(attributes);
        } else if (Array.isArray(attributes)) {
          attributes = Object.fromEntries(attributes);
        } else if (!attributes || typeof attributes !== 'object') {
          attributes = {};
        }
        
        // Handle metadata - convert Map to object if needed, otherwise use as-is
        let metadata = v.metadata;
        if (metadata instanceof Map) {
          metadata = Object.fromEntries(metadata);
        } else if (Array.isArray(metadata)) {
          metadata = Object.fromEntries(metadata);
        } else if (!metadata || typeof metadata !== 'object') {
          metadata = {};
        }
        
        return {
          ...v,
          attributes,
          metadata
        };
      });
      
      setVariations(updatedVariations);
      onVariationsChange && onVariationsChange(updatedVariations);
      
      resetForm();
      setEditingVariation(null);
      setShowAddForm(false);
    } catch (err) {
      logger.error('Error saving variation:', err);
      
      // Extract detailed error messages
      let errorMessage = 'Failed to save variation';
      if (err.response?.data) {
        const errorData = err.response.data;
        if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
          // Show all validation errors
          errorMessage = errorData.message || 'Validation failed';
          const errorDetails = errorData.errors.join(', ');
          setError(`${errorMessage}: ${errorDetails}`);
        } else if (errorData.message) {
          setError(errorData.message);
        } else {
          setError(errorMessage);
        }
      } else if (err.message) {
        setError(err.message);
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (variation) => {
    setEditingVariation(variation);
    setFormData({
      sku: variation.sku || '',
      attributes: variation.attributes || {},
      price: variation.price || '',
      stock: variation.stock || '',
      mainImage: variation.mainImage || '',
      images: variation.images || [],
      description: variation.description || '',
      metadata: variation.metadata || {},
      isDefault: variation.isDefault || false
    });
    setShowAddForm(true);
  };

  const handleDeleteClick = (variationId) => {
    setPendingDeleteId(variationId);
  };

  const handleDeleteConfirm = async () => {
    if (!pendingDeleteId) return;

    try {
      setLoading(true);
      await variationAPI.deleteVariation(pendingDeleteId);
      
      // Refresh variations
      const response = await variationAPI.getProductVariations(productId);
      const updatedVariations = response.data.map(v => {
        // Handle attributes - convert Map to object if needed, otherwise use as-is
        let attributes = v.attributes;
        if (attributes instanceof Map) {
          attributes = Object.fromEntries(attributes);
        } else if (Array.isArray(attributes)) {
          attributes = Object.fromEntries(attributes);
        } else if (!attributes || typeof attributes !== 'object') {
          attributes = {};
        }
        
        // Handle metadata - convert Map to object if needed, otherwise use as-is
        let metadata = v.metadata;
        if (metadata instanceof Map) {
          metadata = Object.fromEntries(metadata);
        } else if (Array.isArray(metadata)) {
          metadata = Object.fromEntries(metadata);
        } else if (!metadata || typeof metadata !== 'object') {
          metadata = {};
        }
        
        return {
          ...v,
          attributes,
          metadata
        };
      });
      
      setVariations(updatedVariations);
      onVariationsChange && onVariationsChange(updatedVariations);
      setPendingDeleteId(null);
    } catch (err) {
      logger.error('Error deleting variation:', err);
      setError(err.response?.data?.message || err.message || 'Failed to delete variation');
      setPendingDeleteId(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setPendingDeleteId(null);
  };

  const handleCancel = () => {
    resetForm();
    setEditingVariation(null);
    setShowAddForm(false);
  };

  return (
    <div className="variation-management">
      <div className="variation-management-header">
        <h3>Product Variations</h3>
        <button
          type="button"
          className="add-variation-btn"
          onClick={() => {
            resetForm();
            setShowAddForm(true);
            setEditingVariation(null);
          }}
          disabled={loading}
        >
          <Plus size={18} />
          Add Variation
        </button>
      </div>

      {error && (
        <div className="variation-error">
          {error}
        </div>
      )}

      {showAddForm && (
        <div className="variation-form">
          <div className="form-header">
            <h4>{editingVariation ? 'Edit Variation' : 'Add New Variation'}</h4>
            <button type="button" className="close-btn" onClick={handleCancel}>
              <X size={20} />
            </button>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>SKU (optional, auto-generated if empty)</label>
              <input
                type="text"
                name="sku"
                value={formData.sku}
                onChange={handleInputChange}
                placeholder="Auto-generated"
              />
            </div>

            <div className="form-group">
              <label>Price *</label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                required
              />
            </div>

            <div className="form-group">
              <label>Stock *</label>
              <input
                type="number"
                name="stock"
                value={formData.stock}
                onChange={handleInputChange}
                min="0"
                required
              />
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="isDefault"
                  checked={formData.isDefault}
                  onChange={handleInputChange}
                />
                Set as default variation
              </label>
            </div>
          </div>

          <div className="form-group">
            <label>Attributes *</label>
            <div className="attributes-input">
              <input
                type="text"
                placeholder="Attribute name (e.g., Color)"
                value={newAttributeKey}
                onChange={(e) => setNewAttributeKey(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAttributeAdd()}
              />
              <input
                type="text"
                placeholder="Attribute value (e.g., Red)"
                value={newAttributeValue}
                onChange={(e) => setNewAttributeValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAttributeAdd()}
              />
              <button type="button" onClick={handleAttributeAdd} className="add-attr-btn">
                Add
              </button>
            </div>
            <div className="attributes-list">
              {Object.entries(formData.attributes).map(([key, value]) => (
                <div key={key} className="attribute-tag">
                  <span><strong>{key}:</strong> {value}</span>
                  <button type="button" onClick={() => handleAttributeRemove(key)}>
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="3"
              placeholder="Optional variation-specific description"
            />
          </div>

          <div className="form-group">
            <label>Main Image</label>
            <div className="image-upload">
              {formData.mainImage ? (
                <div className="image-preview">
                  <img src={formData.mainImage} alt="Main" />
                  <button type="button" onClick={() => handleImageRemove(0, true)}>
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <label className="upload-label">
                  <ImageIcon size={24} />
                  <span>Upload Main Image</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, true)}
                    style={{ display: 'none' }}
                  />
                </label>
              )}
              <button
                type="button"
                className="gallery-select-btn"
                onClick={() => {
                  setGalleryMode('main');
                  setShowImageGallery(true);
                }}
              >
                <ImageIcon size={18} />
                <span>Select from Gallery</span>
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>Additional Images</label>
            <div className="images-grid">
              {formData.images.map((img, index) => (
                <div key={index} className="image-preview">
                  <img src={img} alt={`Additional ${index + 1}`} />
                  <button type="button" onClick={() => handleImageRemove(index)}>
                    <X size={16} />
                  </button>
                </div>
              ))}
              <label className="upload-label">
                <ImageIcon size={24} />
                <span>Add Image</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
              </label>
              <button
                type="button"
                className="gallery-select-btn"
                onClick={() => {
                  setGalleryMode('gallery');
                  setShowImageGallery(true);
                }}
              >
                <ImageIcon size={18} />
                <span>Select from Gallery</span>
              </button>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={handleCancel} className="cancel-btn" disabled={loading}>
              Cancel
            </button>
            <button type="button" onClick={handleSave} className="save-btn" disabled={loading}>
              <Save size={18} />
              {loading ? 'Saving...' : 'Save Variation'}
            </button>
          </div>
        </div>
      )}

      <div className="variations-list">
        {variations.length === 0 ? (
          <p className="no-variations">No variations added yet. Click "Add Variation" to create one.</p>
        ) : (
          variations.map((variation) => (
            <div key={variation._id} className="variation-item">
              <div className="variation-item-header">
                <div>
                  <h4>
                    {variation.sku}
                    {variation.isDefault && <span className="default-badge">Default</span>}
                  </h4>
                  <p className="variation-attributes">
                    {Object.entries(variation.attributes || {}).map(([key, value]) => (
                      <span key={key} className="attr-badge">
                        {key}: {value}
                      </span>
                    ))}
                  </p>
                </div>
                <div className="variation-actions">
                  <button type="button" onClick={() => handleEdit(variation)} className="edit-btn">
                    <Edit size={16} />
                  </button>
                  <button type="button" onClick={() => handleDeleteClick(variation._id)} className="delete-btn">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="variation-item-details">
                <div className="detail-item">
                  <strong>Price:</strong> ₱{variation.price}
                </div>
                <div className="detail-item">
                  <strong>Stock:</strong> {variation.stock}
                </div>
                {variation.description && (
                  <div className="detail-item">
                    <strong>Description:</strong> {variation.description}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Image Gallery Modal for variations */}
      {showImageGallery && (
        <ImageGalleryPage
          isModal={true}
          allowMultiSelect={galleryMode === 'gallery'}
          onClose={() => setShowImageGallery(false)}
          onSelectImage={(imageUrl) => {
            if (galleryMode === 'main') {
              setFormData(prev => ({ ...prev, mainImage: imageUrl }));
            } else {
              setFormData(prev => ({ ...prev, images: [...prev.images, imageUrl] }));
            }
            setShowImageGallery(false);
          }}
          onSelectImages={(imageUrls) => {
            if (galleryMode === 'gallery') {
              setFormData(prev => ({ ...prev, images: [...prev.images, ...imageUrls] }));
            }
            setShowImageGallery(false);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={pendingDeleteId !== null}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Variation"
        message="Are you sure you want to delete this variation? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="warning"
      />
    </div>
  );
};

export default VariationManagement;

